import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { BrowserMultiFormatReader } from "@zxing/browser";
import * as zxingLibrary from "@zxing/library";
const { BarcodeFormat, DecodeHintType } = zxingLibrary;
import type { IScannerControls } from "@zxing/browser";
import { verifyEstimatorPassword } from "@/lib/estimator.functions";
import {
  listInventory,
  scanInventory,
  updateInventoryItem,
  deleteInventoryItem,
  lookupUpc,
  transferInventory,
  analyzeReceipt,
  bulkAddInventory,
} from "@/lib/inventory.functions";
import { getStoredPassword, setStoredPassword } from "@/lib/estimator-session";
import { Lock, ArrowRight, Camera, Plus, Minus, Trash2, X, Loader2, Search, AlertTriangle, Truck, Warehouse, ArrowLeftRight, Receipt, Upload } from "lucide-react";


type Location = "van" | "warehouse";

export const Route = createFileRoute("/inventory")({
  head: () => ({
    meta: [
      { title: "Van Inventory | Apex AV" },
      { name: "description", content: "Private van inventory scanner." },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: InventoryPage,
});

type Item = {
  id: string;
  upc: string;
  name: string;
  quantity: number;
  min_quantity: number;
  notes: string | null;
  location: Location;
  updated_at: string;
};

function InventoryPage() {
  const [unlocked, setUnlocked] = useState(false);
  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      sessionStorage.getItem("apex_estimator_unlocked") === "1" &&
      getStoredPassword()
    ) {
      setUnlocked(true);
    }
  }, []);
  return unlocked ? <Inventory /> : <PasswordGate onUnlock={() => setUnlocked(true)} />;
}

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const verify = useServerFn(verifyEstimatorPassword);
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await verify({ data: { password } });
      if (res.ok) {
        setStoredPassword(password);
        onUnlock();
      } else setError("Incorrect password.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="container mx-auto px-6 py-24 flex items-center justify-center min-h-[70vh]">
      <form
        onSubmit={submit}
        className="w-full max-w-md rounded-2xl border border-border bg-card p-10 shadow-card"
      >
        <div className="h-12 w-12 rounded-lg bg-gradient-brand flex items-center justify-center shadow-glow">
          <Lock className="h-6 w-6 text-brand-foreground" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold">Van inventory</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Enter the password provided by Apex AV to access the inventory.
        </p>
        <label className="block mt-8 text-sm font-medium">Password</label>
        <input
          type="password"
          aria-label="Password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="mt-2 w-full rounded-md border border-border bg-background px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        {error && <p className="mt-3 text-sm text-destructive">{error}</p>}
        <button
          type="submit"
          disabled={loading || !password}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-gradient-brand px-6 py-3 text-sm font-semibold text-brand-foreground shadow-glow disabled:opacity-50"
        >
          {loading ? "Verifying…" : "Unlock inventory"} <ArrowRight className="h-4 w-4" />
        </button>
      </form>
    </section>
  );
}

function Inventory() {
  const pw = getStoredPassword() ?? "";
  const listFn = useServerFn(listInventory);
  const scanFn = useServerFn(scanInventory);
  const updateFn = useServerFn(updateInventoryItem);
  const deleteFn = useServerFn(deleteInventoryItem);
  const lookupFn = useServerFn(lookupUpc);
  const transferFn = useServerFn(transferInventory);

  const [allItems, setAllItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [mode, setMode] = useState<"in" | "out">("in");
  const [location, setLocation] = useState<Location>("van");
  const [manualUpc, setManualUpc] = useState("");
  const [msg, setMsg] = useState<{ text: string; kind: "ok" | "err" } | null>(null);
  const [query, setQuery] = useState("");
  const [transferItem, setTransferItem] = useState<Item | null>(null);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const analyzeFn = useServerFn(analyzeReceipt);
  const bulkAddFn = useServerFn(bulkAddInventory);


  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listFn({ data: { password: pw } });
      setAllItems(res.items as Item[]);
    } catch (e) {
      setMsg({ text: e instanceof Error ? e.message : "Load failed", kind: "err" });
    } finally {
      setLoading(false);
    }
  }, [listFn, pw]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const items = allItems.filter((i) => i.location === location);

  const flash = (text: string, kind: "ok" | "err" = "ok") => {
    setMsg({ text, kind });
    setTimeout(() => setMsg(null), 2500);
  };

  async function handleScannedCode(upc: string) {
    const delta = mode === "in" ? 1 : -1;
    try {
      const existing = items.find((i) => i.upc === upc);
      let name: string | undefined;
      let notes: string | undefined;
      if (!existing) {
        // Reuse name from the other location if this UPC already exists there
        const sibling = allItems.find((i) => i.upc === upc);
        if (sibling) {
          name = sibling.name;
        } else {
          try {
            const lookup = await lookupFn({ data: { password: pw, upc } });
            if (lookup.found) {
              name = lookup.name;
              if (lookup.notes) notes = lookup.notes;
            }
          } catch {
            // ignore lookup failure, fall through to prompt
          }
          if (!name) {
            name = window.prompt(`New item ${upc}. Enter a name (optional):`) ?? "";
          }
        }
      }
      const res = await scanFn({ data: { password: pw, upc, delta, location, name, notes } });
      const tag = !existing && name ? ` (auto: ${name})` : "";
      flash(
        `${mode === "in" ? "+1" : "-1"} ${res.item.name || res.item.upc} → ${res.item.quantity} (${location})${tag}`,
        "ok",
      );
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Scan failed", "err");
    }
  }

  async function submitManual(e: React.FormEvent) {
    e.preventDefault();
    const upc = manualUpc.trim();
    if (!upc) return;
    setManualUpc("");
    await handleScannedCode(upc);
  }

  async function adjustQty(item: Item, delta: number) {
    try {
      await scanFn({ data: { password: pw, upc: item.upc, delta, location: item.location } });
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Update failed", "err");
    }
  }

  async function doTransfer(item: Item, qty: number) {
    const to: Location = item.location === "van" ? "warehouse" : "van";
    try {
      await transferFn({ data: { password: pw, upc: item.upc, from: item.location, to, quantity: qty } });
      flash(`Transferred ${qty} × ${item.name || item.upc} → ${to}`, "ok");
      setTransferItem(null);
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Transfer failed", "err");
    }
  }

  async function editItem(item: Item) {
    const name = window.prompt("Item name:", item.name);
    if (name === null) return;
    try {
      await updateFn({ data: { password: pw, id: item.id, name } });
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Update failed", "err");
    }
  }

  async function editMin(item: Item) {
    const raw = window.prompt(
      `Minimum quantity for ${item.name || item.upc}:\n(you'll be alerted when stock drops to this level)`,
      String(item.min_quantity ?? 0),
    );
    if (raw === null) return;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 0) {
      flash("Enter a whole number ≥ 0", "err");
      return;
    }
    try {
      await updateFn({ data: { password: pw, id: item.id, min_quantity: n } });
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Update failed", "err");
    }
  }

  async function removeItem(item: Item) {
    if (!window.confirm(`Delete ${item.name || item.upc}?`)) return;
    try {
      await deleteFn({ data: { password: pw, id: item.id } });
      await refresh();
    } catch (e) {
      flash(e instanceof Error ? e.message : "Delete failed", "err");
    }
  }

  const isLow = (i: Item) => i.min_quantity > 0 && i.quantity <= i.min_quantity;
  const lowCount = items.filter(isLow).length;
  const filtered = items.filter((i) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return i.upc.toLowerCase().includes(q) || i.name.toLowerCase().includes(q);
  });

  return (
    <section className="container mx-auto px-4 md:px-6 py-8 md:py-12 max-w-4xl">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold">
            {location === "van" ? "Van" : "Warehouse"} Inventory
          </h1>
          <p className="text-sm text-muted-foreground">Scan UPC codes to add or remove stock.</p>
        </div>
        <div className="flex gap-2 rounded-md border border-border p-1">
          <button
            onClick={() => setMode("in")}
            className={`px-3 py-1.5 rounded text-sm font-medium ${mode === "in" ? "bg-green-600 text-white" : "text-muted-foreground"}`}
          >
            <Plus className="inline h-4 w-4 mr-1" />
            Scan IN
          </button>
          <button
            onClick={() => setMode("out")}
            className={`px-3 py-1.5 rounded text-sm font-medium ${mode === "out" ? "bg-red-600 text-white" : "text-muted-foreground"}`}
          >
            <Minus className="inline h-4 w-4 mr-1" />
            Scan OUT
          </button>
        </div>
      </header>

      <div className="mt-4 inline-flex rounded-md border border-border p-1">
        <button
          onClick={() => setLocation("van")}
          className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${location === "van" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >
          <Truck className="h-4 w-4" /> Van
          <span className="ml-1 text-xs opacity-70">
            ({allItems.filter((i) => i.location === "van").length})
          </span>
        </button>
        <button
          onClick={() => setLocation("warehouse")}
          className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${location === "warehouse" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
        >
          <Warehouse className="h-4 w-4" /> Warehouse
          <span className="ml-1 text-xs opacity-70">
            ({allItems.filter((i) => i.location === "warehouse").length})
          </span>
        </button>
      </div>

      {msg && (
        <div
          className={`mt-4 rounded-md px-4 py-3 text-sm font-medium ${
            msg.kind === "ok"
              ? "bg-green-500/15 text-green-700 dark:text-green-400"
              : "bg-destructive/15 text-destructive"
          }`}
        >
          {msg.text}
        </div>
      )}

      {lowCount > 0 && !loading && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm font-medium text-amber-700 dark:text-amber-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {lowCount} item{lowCount === 1 ? "" : "s"} at or below minimum stock.
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => setScannerOpen(true)}
          className={`flex-1 inline-flex items-center justify-center gap-2 rounded-md px-6 py-4 text-base font-semibold text-white shadow ${
            mode === "in" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
          }`}
        >
          <Camera className="h-5 w-5" /> Open camera scanner
        </button>
        <form onSubmit={submitManual} className="flex-1 flex gap-2">
          <input
            type="text"
            inputMode="numeric"
            placeholder="Type UPC…"
            value={manualUpc}
            onChange={(e) => setManualUpc(e.target.value)}
            className="flex-1 rounded-md border border-border bg-background px-3 py-3 text-base focus:outline-none focus:ring-2 focus:ring-brand"
          />
          <button
            type="submit"
            disabled={!manualUpc.trim()}
            className="rounded-md bg-primary px-4 py-3 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            Enter
          </button>
        </form>
      </div>

      <button
        onClick={() => setReceiptOpen(true)}
        className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-card px-4 py-3 text-sm font-semibold hover:bg-accent"
      >
        <Receipt className="h-4 w-4" /> Scan or upload receipt/invoice
      </button>


      <div className="mt-8">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h2 className="font-display text-lg font-semibold">
            Items {loading ? "" : `(${items.length})`}
          </h2>
          <div className="relative w-48">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            />
          </div>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground">No items yet. Scan a UPC to get started.</p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
            {filtered.map((item) => {
              const low = isLow(item);
              const empty = item.quantity === 0 && item.min_quantity > 0;
              return (
                <li
                  key={item.id}
                  className={`flex items-center gap-3 p-3 transition-colors ${
                    empty
                      ? "bg-red-500/15 border-l-4 border-l-red-500"
                      : low
                        ? "bg-amber-500/10 border-l-4 border-l-amber-500"
                        : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {low && (
                        <AlertTriangle
                          className={`h-4 w-4 shrink-0 ${empty ? "text-red-600" : "text-amber-600"}`}
                        />
                      )}
                      <button
                        onClick={() => editItem(item)}
                        className="text-left font-medium truncate hover:underline"
                      >
                        {item.name || <span className="text-muted-foreground">Unnamed</span>}
                      </button>
                    </div>
                    <div className="text-xs text-muted-foreground truncate flex items-center gap-2">
                      <span>UPC {item.upc}</span>
                      <span>·</span>
                      <button
                        onClick={() => editMin(item)}
                        className={`hover:underline ${low ? "font-semibold text-amber-700 dark:text-amber-400" : ""}`}
                        aria-label="Set minimum quantity"
                      >
                        min {item.min_quantity}
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => adjustQty(item, -1)}
                      aria-label="Remove one"
                      className="h-8 w-8 rounded-md border border-border hover:bg-accent"
                    >
                      <Minus className="inline h-4 w-4" />
                    </button>
                    <span
                      className={`w-10 text-center font-mono text-lg font-bold ${
                        empty ? "text-red-600" : low ? "text-amber-700 dark:text-amber-400" : ""
                      }`}
                    >
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => adjustQty(item, 1)}
                      aria-label="Add one"
                      className="h-8 w-8 rounded-md border border-border hover:bg-accent"
                    >
                      <Plus className="inline h-4 w-4" />
                    </button>
                  </div>
                  <button
                    onClick={() => setTransferItem(item)}
                    aria-label={`Transfer to ${item.location === "van" ? "warehouse" : "van"}`}
                    title={`Transfer to ${item.location === "van" ? "warehouse" : "van"}`}
                    disabled={item.quantity === 0}
                    className="h-8 w-8 rounded-md border border-border hover:bg-accent flex items-center justify-center disabled:opacity-40"
                  >
                    <ArrowLeftRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeItem(item)}
                    aria-label="Delete item"
                    className="h-8 w-8 rounded-md text-destructive hover:bg-destructive/10 flex items-center justify-center"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>


      {scannerOpen && (
        <ScannerModal
          mode={mode}
          location={location}
          onClose={() => setScannerOpen(false)}
          onDetected={handleScannedCode}
        />
      )}

      {transferItem && (
        <TransferModal
          item={transferItem}
          onClose={() => setTransferItem(null)}
          onConfirm={(qty) => doTransfer(transferItem, qty)}
        />
      )}

      {receiptOpen && (
        <ReceiptModal
          location={location}
          onClose={() => setReceiptOpen(false)}
          analyze={async (files) => analyzeFn({ data: { password: pw, files } })}
          commit={async (items, source, destination) => {
            const res = await bulkAddFn({
              data: { password: pw, location: destination, items, source },
            });
            flash(`Added ${res.added} new, updated ${res.updated} existing in ${destination === "van" ? "Van" : "Warehouse"}.`, "ok");
            setReceiptOpen(false);
            await refresh();
          }}

        />
      )}
    </section>
  );
}


function ScannerModal({
  mode,
  location,
  onClose,
  onDetected,
}: {
  mode: "in" | "out";
  location: Location;
  onClose: () => void;
  onDetected: (upc: string) => Promise<void>;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const lastScanRef = useRef<{ code: string; ts: number }>({ code: "", ts: 0 });
  const [error, setError] = useState<string | null>(null);
  const [lastCode, setLastCode] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.UPC_A,
      BarcodeFormat.UPC_E,
      BarcodeFormat.EAN_13,
      BarcodeFormat.EAN_8,
      BarcodeFormat.CODE_128,
      BarcodeFormat.CODE_39,
      BarcodeFormat.ITF,
    ]);
    const reader = new BrowserMultiFormatReader(hints);

    (async () => {
      try {
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: "environment" } } },
          videoRef.current!,
          (result) => {
            if (!result) return;
            const code = result.getText();
            const now = Date.now();
            if (lastScanRef.current.code === code && now - lastScanRef.current.ts < 1800) return;
            lastScanRef.current = { code, ts: now };
            setLastCode(code);
            if (navigator.vibrate) navigator.vibrate(80);
            void onDetected(code);
          },
        );
        if (cancelled) controls.stop();
        else controlsRef.current = controls;
      } catch (e) {
        setError(
          e instanceof Error
            ? `${e.message}. Grant camera permission and use HTTPS.`
            : "Camera error",
        );
      }
    })();

    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [onDetected]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <span
              className={`h-2.5 w-2.5 rounded-full ${mode === "in" ? "bg-green-500" : "bg-red-500"}`}
            />
            <span className="font-medium">
              Scanning {mode === "in" ? "IN" : "OUT"} · {location === "van" ? "Van" : "Warehouse"}
            </span>
          </div>
          <button
            onClick={onClose}
            aria-label="Close scanner"
            className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="relative bg-black aspect-[4/3]">
          <video
            ref={videoRef}
            className="h-full w-full object-cover"
            playsInline
            muted
            autoPlay
          />
          <div className="pointer-events-none absolute inset-8 border-2 border-white/70 rounded-lg" />
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-4 bg-black/70 text-white text-sm text-center">
              {error}
            </div>
          )}
        </div>
        <div className="p-4 text-sm">
          {lastCode ? (
            <p>
              Last scan:{" "}
              <span className="font-mono font-semibold">{lastCode}</span>
            </p>
          ) : (
            <p className="text-muted-foreground">Point the camera at a UPC barcode.</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Keep scanning — the modal stays open until you close it.
          </p>
        </div>
      </div>
    </div>
  );
}

function TransferModal({
  item,
  onClose,
  onConfirm,
}: {
  item: Item;
  onClose: () => void;
  onConfirm: (qty: number) => Promise<void>;
}) {
  const to: Location = item.location === "van" ? "warehouse" : "van";
  const [qty, setQty] = useState<string>("1");
  const [busy, setBusy] = useState(false);

  const parsed = parseInt(qty, 10);
  const clamped = Math.max(1, Math.min(item.quantity, isNaN(parsed) ? 1 : parsed));

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await onConfirm(clamped);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl bg-card border border-border p-6"
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Transfer stock</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {item.name || item.upc}
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 text-sm font-medium">
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5">
            {item.location === "van" ? <Truck className="h-4 w-4" /> : <Warehouse className="h-4 w-4" />}
            {item.location === "van" ? "Van" : "Warehouse"} ({item.quantity})
          </span>
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5">
            {to === "van" ? <Truck className="h-4 w-4" /> : <Warehouse className="h-4 w-4" />}
            {to === "van" ? "Van" : "Warehouse"}
          </span>
        </div>
        <label className="block mt-5 text-sm font-medium">Quantity to transfer</label>
        <input
          type="number" onFocus={(e)=>(e.target as HTMLInputElement).select()}
          min={1}
          max={item.quantity}
          value={qty}
          onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))}
          inputMode="numeric"
          className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <div className="mt-6 flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || item.quantity < 1}
            className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowLeftRight className="h-4 w-4" />}
            Transfer {clamped}
          </button>
        </div>
      </form>
    </div>
  );
}

type ReceiptLine = {
  name: string;
  quantity: number;
  unit_price: number;
  upc: string;
  keep: boolean;
};

function ReceiptModal({
  location,
  onClose,
  analyze,
  commit,
}: {
  location: Location;
  onClose: () => void;
  analyze: (
    files: { fileName: string; mimeType: string; dataBase64: string }[],
  ) => Promise<{ vendor: string; date: string; items: { name: string; quantity: number; unit_price: number; upc: string }[] }>;
  commit: (
    items: { name: string; quantity: number; upc?: string }[],
    source: string,
    destination: Location,
  ) => Promise<void>;

}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [vendor, setVendor] = useState("");
  const [date, setDate] = useState("");
  const [lines, setLines] = useState<ReceiptLine[] | null>(null);
  const [destination, setDestination] = useState<Location>(location);

  const fileRef = useRef<HTMLInputElement | null>(null);
  const cameraRef = useRef<HTMLInputElement | null>(null);

  async function handleFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const files = await Promise.all(
        Array.from(fileList)
          .slice(0, 4)
          .map(
            (f) =>
              new Promise<{ fileName: string; mimeType: string; dataBase64: string }>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => {
                  const result = reader.result as string;
                  const b64 = result.split(",")[1] ?? "";
                  resolve({ fileName: f.name, mimeType: f.type || "application/octet-stream", dataBase64: b64 });
                };
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(f);
              }),
          ),
      );
      const res = await analyze(files);
      setVendor(res.vendor);
      setDate(res.date);
      setLines(
        res.items.map((it) => ({
          name: it.name,
          quantity: it.quantity,
          unit_price: it.unit_price,
          upc: it.upc,
          keep: true,
        })),
      );
      if (res.items.length === 0) {
        setError("No line items detected. Try a clearer photo.");
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Analysis failed");
    } finally {
      setBusy(false);
    }
  }

  function updateLine(idx: number, patch: Partial<ReceiptLine>) {
    setLines((prev) => (prev ? prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)) : prev));
  }

  async function onCommit() {
    if (!lines) return;
    const kept = lines
      .filter((l) => l.keep && l.name.trim() && l.quantity > 0)
      .map((l) => ({ name: l.name.trim(), quantity: l.quantity, upc: l.upc || undefined }));
    if (kept.length === 0) {
      setError("Select at least one item to add.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const source = [vendor, date].filter(Boolean).join(" · ");
      await commit(kept, source, destination);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Commit failed");
      setBusy(false);
    }
  }

  const keptCount = lines?.filter((l) => l.keep).length ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl max-h-[92vh] flex flex-col rounded-2xl bg-card border border-border overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            <span className="font-medium">Receipt import</span>

          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 rounded-md hover:bg-accent flex items-center justify-center"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!lines && !busy && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Take a photo of a receipt or upload an image / PDF. The AI extracts line items and quantities for you to confirm.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraRef.current?.click()}
                  className="inline-flex flex-col items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-6 hover:bg-accent"
                >
                  <Camera className="h-6 w-6" />
                  <span className="text-sm font-semibold">Take photo</span>
                </button>
                <button
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex flex-col items-center justify-center gap-2 rounded-md border border-border bg-background px-4 py-6 hover:bg-accent"
                >
                  <Upload className="h-6 w-6" />
                  <span className="text-sm font-semibold">Upload file</span>
                </button>
              </div>
              <input
                ref={cameraRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => handleFiles(e.target.files)}
              />
            </div>
          )}

          {busy && !lines && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Analyzing receipt…</p>
            </div>
          )}

          {lines && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <label className="text-xs font-medium text-muted-foreground">
                  Vendor
                  <input
                    value={vendor}
                    onChange={(e) => setVendor(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  />
                </label>
                <label className="text-xs font-medium text-muted-foreground">
                  Date
                  <input
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
                  />
                </label>
              </div>

              <div>
                <div className="text-xs font-medium text-muted-foreground mb-1">Add to</div>
                <div className="inline-flex rounded-md border border-border bg-background p-0.5">
                  <button
                    type="button"
                    onClick={() => setDestination("van")}
                    className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${destination === "van" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    <Truck className="h-4 w-4" /> Van
                  </button>
                  <button
                    type="button"
                    onClick={() => setDestination("warehouse")}
                    className={`px-3 py-1.5 rounded text-sm font-medium inline-flex items-center gap-1.5 ${destination === "warehouse" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  >
                    <Warehouse className="h-4 w-4" /> Warehouse
                  </button>
                </div>
              </div>


              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {keptCount} of {lines.length} selected
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setLines(lines.map((l) => ({ ...l, keep: true })))}
                    className="hover:underline"
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setLines(lines.map((l) => ({ ...l, keep: false })))}
                    className="hover:underline"
                  >
                    Select none
                  </button>
                </div>
              </div>

              <ul className="divide-y divide-border rounded-md border border-border overflow-hidden">
                {lines.map((line, idx) => (
                  <li
                    key={idx}
                    className={`flex items-center gap-2 p-2 ${line.keep ? "" : "opacity-50"}`}
                  >
                    <input
                      type="checkbox"
                      checked={line.keep}
                      onChange={(e) => updateLine(idx, { keep: e.target.checked })}
                      className="h-4 w-4"
                    />
                    <input
                      type="number"
                      inputMode="decimal"
                      min={1}
                      value={line.quantity === 0 ? "" : line.quantity}
                      onFocus={(e)=>(e.target as HTMLInputElement).select()}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "") { updateLine(idx, { quantity: 0 }); return; }
                        const n = parseInt(v);
                        if (!Number.isNaN(n)) updateLine(idx, { quantity: Math.max(0, n) });
                      }}
                      onBlur={(e) => {
                        if (e.target.value === "" || parseInt(e.target.value) < 1) updateLine(idx, { quantity: 1 });
                      }}
                      className="w-16 rounded-md border border-border bg-background px-2 py-1 text-sm"
                    />
                    <input
                      value={line.name}
                      onChange={(e) => updateLine(idx, { name: e.target.value })}
                      className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm"
                      placeholder="Product name"
                    />
                    <button
                      onClick={() => setLines(lines.filter((_, i) => i !== idx))}
                      aria-label="Remove line"
                      className="h-7 w-7 rounded-md text-destructive hover:bg-destructive/10 flex items-center justify-center"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
              </ul>

              <button
                onClick={() =>
                  setLines([
                    ...lines,
                    { name: "", quantity: 1, unit_price: 0, upc: "", keep: true },
                  ])
                }
                className="text-xs font-medium hover:underline"
              >
                + Add another line
              </button>
            </div>
          )}

          {error && (
            <p className="mt-3 text-sm text-destructive">{error}</p>
          )}
        </div>

        {lines && (
          <div className="flex items-center justify-end gap-2 border-t border-border p-3">
            <button
              onClick={onClose}
              className="rounded-md border border-border px-4 py-2 text-sm font-medium hover:bg-accent"
            >
              Cancel
            </button>
            <button
              onClick={onCommit}
              disabled={busy || keptCount === 0}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Add {keptCount} to {destination === "van" ? "Van" : "Warehouse"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
